import { AlertCircle, Check, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "utils";

const TONES = {
  error: {
    box: "border-status-error bg-status-error/15",
    icon: AlertCircle,
  },
  warning: {
    box: "border-status-warning bg-status-warning/15",
    icon: TriangleAlert,
  },
  success: {
    box: "border-status-success bg-status-success/15",
    icon: Check,
  },
} as const;

/**
 * An in-page banner for a result the operator has to read: a failed command, a
 * truncated list, a partial bulk delete.
 *
 * The meaning is carried by the icon and the words, not by the colour alone,
 * and the body stays in the foreground colour so it reads in both themes. A
 * flat bright fill cannot do that: yellow under white text is 1.7:1.
 */
export function Notice({
  tone,
  role = tone === "error" ? "alert" : "status",
  children,
  action,
  className,
  testId,
}: {
  tone: keyof typeof TONES;
  role?: "alert" | "status";
  children: ReactNode;
  /** A trailing control, such as a dismiss button. */
  action?: ReactNode;
  className?: string;
  testId?: string;
}) {
  const { box, icon: Icon } = TONES[tone];
  return (
    <div
      role={role}
      className={cn(
        "flex items-start gap-2 rounded-lg border px-3 py-2 text-sm text-foreground wrap-anywhere",
        box,
        className,
      )}
      data-testid={testId}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
      {action}
    </div>
  );
}
